import { useEffect, useRef, useState } from 'react';
import { Aperture, Camera, Mic, MicOff, RotateCw, Trash2 } from 'lucide-react';
import { useStore, isPremium } from '../store/useStore';
import { ScanCameraModal } from '../components/ScanCameraModal';
import { useDictation } from '../hooks/useDictation';
import { DICTATION_HINT } from '../engine/dictationText';
import type { ScanAttachment } from '../types';

const PLACEHOLDER =
  "e.g., Fix the severe login server crash bug. Review the draft consulting contract for next month. Clear out random marketing emails. Mindlessly scroll social media because I'm overwhelmed...";

/** Onboarding Screen 2 — the frictionless brain dump (type / dictate / scan). */
export function DumpScreen() {
  const runMagicSort = useStore((s) => s.runMagicSort);
  const providerNotice = useStore((s) => s.providerNotice);
  const importStrategy = useStore((s) => s.importStrategy);
  const setImportStrategy = useStore((s) => s.setImportStrategy);
  const scans = useStore((s) => s.scans);
  const attachScan = useStore((s) => s.attachScan);
  const removeScan = useStore((s) => s.removeScan);
  const updateScan = useStore((s) => s.updateScan);
  const scansRemaining = useStore((s) => s.scansRemaining);
  const subscription = useStore((s) => s.subscription);
  const openScanPack = useStore((s) => s.openScanPack);
  const onboarded = useStore((s) => s.onboarded);

  // Draft lives in the store, persisted as typed (V2 §4a-capture): a wrong
  // tap, back-press, or app switch never loses a list. Cleared only by a
  // successful organize (see runMagicSort).
  const text = useStore((s) => s.dumpDraft);
  const setText = useStore((s) => s.setDumpDraft);
  const [batchSticky, setBatchSticky] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const voice = useDictation(setText, { lines: true });
  const premium = isPremium(subscription);

  const requestScan = () => {
    // Tokens consume on successful AI reads only; attached-but-unprocessed
    // photos reserve them (matches attachScan's gate).
    if (!premium && scansRemaining <= scans.length) {
      openScanPack();
      return;
    }
    setCameraOpen(true);
  };

  // Arrived from the board's camera button (10 Oct 2026): open the scanner
  // straight away — scanning must be one tap from the board, not a scroll
  // to the bottom of it. One-shot; the free-tier token gate still applies.
  const consumeScanOnOpen = useStore((s) => s.consumeScanOnOpen);
  useEffect(() => {
    if (consumeScanOnOpen()) requestScan();
  }, []);

  /**
   * Downscale gallery photos before attaching. Full-resolution camera shots
   * (often 10MB+) slow mobile uploads and inflate the AI payload; ~1600px
   * keeps handwriting perfectly readable for OCR at a fraction of the size.
   *
   * EXIF: phone cameras store sideways pixels + an orientation flag. We
   * decode with imageOrientation:'from-image' so the flag is APPLIED before
   * we re-encode — otherwise the AI receives the photo rotated 90° (a real
   * tester-reported failure). Fallback path uses <img>, which modern
   * browsers also orient. The thumbnail ↻ button covers photos whose pixels
   * are genuinely sideways with no flag at all.
   */
  const decodeOriented = async (file: File): Promise<ImageBitmap | HTMLImageElement> => {
    if (typeof createImageBitmap === 'function') {
      try {
        return await createImageBitmap(file, {
          imageOrientation: 'from-image',
        } as ImageBitmapOptions);
      } catch {
        /* fall through to <img> decoding */
      }
    }
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('unreadable image'));
      };
      img.src = url;
    });
  };

  const downscale = async (file: File): Promise<ScanAttachment> => {
    const img = await decodeOriented(file);
    const maxSide = 1600;
    const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    return {
      mimeType: 'image/jpeg',
      dataBase64: dataUrl.split(',')[1] ?? '',
      previewUrl: dataUrl,
      fileName: file.name,
      batchStickyMode: batchSticky,
    };
  };

  /** Rotate an attached scan 90° clockwise — the user's manual override
   *  when a photo arrives sideways (no scan token consumed). */
  const rotateScan = (index: number) => {
    const sc = scans[index];
    if (!sc) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.height;
      canvas.height = img.width;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      updateScan(index, {
        ...sc,
        mimeType: 'image/jpeg',
        dataBase64: dataUrl.split(',')[1] ?? '',
        previewUrl: dataUrl,
      });
    };
    img.src = sc.previewUrl;
  };

  const handleFiles = (files: FileList | null) => {
    for (const file of Array.from(files ?? [])) {
      void downscale(file)
        .then(attachScan)
        .catch(() => {
          // Format the browser can't decode (e.g. HEIC on some platforms) —
          // fall back to attaching the original file untouched.
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = reader.result as string;
            attachScan({
              mimeType: file.type || 'image/jpeg',
              dataBase64: dataUrl.split(',')[1] ?? '',
              previewUrl: dataUrl,
              fileName: file.name,
              batchStickyMode: batchSticky,
            });
          };
          reader.readAsDataURL(file);
        });
    }
  };

  const canSubmit = text.trim().length >= 3 || scans.length > 0;

  return (
    <div className="mx-auto flex min-h-full w-full max-w-md flex-col justify-between px-5 py-8 lg:max-w-3xl lg:justify-center lg:gap-6">
      <div className="space-y-5">
        <div className="space-y-2 text-center">
          <h2 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">
            Capture your clutter
          </h2>
          <p className="mx-auto max-w-xs text-xs leading-relaxed text-slate-500 dark:text-slate-400">
            Type, dictate, or scan a paper list. Don't worry about formatting,
            order, or priority — just dump it here.
          </p>
        </div>

        <div className="relative space-y-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 transition-colors focus-within:border-brand-500">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={7}
            autoFocus
            placeholder={PLACEHOLDER}
            className="w-full resize-none bg-transparent text-xs leading-relaxed text-slate-600 dark:text-slate-300 placeholder:text-slate-500 focus:outline-none"
          />

          {scans.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {scans.map((sc, i) => (
                <div
                  key={i}
                  className="relative h-16 w-16 overflow-hidden rounded-xl border border-slate-300 dark:border-slate-700"
                >
                  <img
                    src={sc.previewUrl}
                    alt={`Scan ${i + 1}`}
                    className="h-full w-full object-cover"
                  />
                  <button
                    onClick={() => removeScan(i)}
                    aria-label={`Remove scan ${i + 1}`}
                    className="absolute right-0.5 top-0.5 rounded-full bg-slate-900/70 p-0.5 text-white hover:bg-red-600"
                  >
                    <Trash2 size={11} />
                  </button>
                  <button
                    onClick={() => rotateScan(i)}
                    aria-label={`Rotate scan ${i + 1}`}
                    title="Rotate 90°"
                    className="absolute bottom-0.5 right-0.5 rounded-full bg-slate-900/70 p-0.5 text-white hover:bg-brand-600"
                  >
                    <RotateCw size={11} />
                  </button>
                </div>
              ))}
              <span className="self-center text-[11px] font-semibold text-brand-600 dark:text-brand-400">
                {scans.length} photo{scans.length === 1 ? '' : 's'} attached
              </span>
            </div>
          )}

          <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-800/60 pt-2 text-[11px] text-slate-500 dark:text-slate-400">
            <label className="flex cursor-pointer items-center gap-1.5">
              <input
                type="checkbox"
                checked={batchSticky}
                onChange={(e) => setBatchSticky(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 text-brand-500 focus:ring-0 focus:ring-offset-0"
              />
              <span className="font-semibold">Batch sticky-notes mode</span>
            </label>
            {!premium && (
              <span className="flex items-center gap-1 font-bold">
                <Aperture size={11} className="text-brand-600 dark:text-brand-400" />
                {Math.max(0, scansRemaining - scans.length)} scan
                {Math.max(0, scansRemaining - scans.length) === 1 ? '' : 's'} left
              </span>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-800/60 pt-3">
            <div className="flex gap-2">
              <button
                onClick={requestScan}
                className="flex items-center gap-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-2.5 py-1.5 text-[11px] font-bold text-brand-600 dark:text-brand-400 transition-colors hover:bg-slate-200 dark:hover:bg-slate-700"
              >
                <Camera size={13} />
                Scan Paper List
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  handleFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </div>
            {voice.supported && (
              <button
                onClick={() => voice.toggle(text)}
                aria-label={voice.listening ? 'Stop dictation' : 'Start dictation'}
                className={`flex h-10 w-10 items-center justify-center rounded-full border transition-all active:scale-95 ${
                  voice.listening
                    ? 'animate-pulse border-red-500 bg-red-600 text-white'
                    : 'border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-brand-600 dark:text-brand-400 hover:bg-brand-600 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {voice.listening ? <MicOff size={16} /> : <Mic size={16} />}
              </button>
            )}
          </div>
        </div>

        {onboarded && (
          /* Strategy is a SETTING, not an action (V2 §4a-capture, register
             #25): a labeled radio group, visually quieter than the CTA — no
             more verb-shaped pseudo-buttons that read as "process now". */
          <fieldset className="space-y-2 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 p-4">
            <legend className="sr-only">When organizing</legend>
            <span className="block text-[11px] font-bold uppercase tracking-widest text-slate-500">
              When organizing:
            </span>
            <div className="space-y-1.5">
              <label className="flex cursor-pointer items-start gap-2 rounded-lg p-1.5 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800/60">
                <input
                  type="radio"
                  name="import-strategy"
                  checked={importStrategy === 'merge'}
                  onChange={() => setImportStrategy('merge')}
                  className="mt-0.5 h-3.5 w-3.5 border-slate-300 dark:border-slate-700 text-brand-500 focus:ring-0 focus:ring-offset-0"
                />
                <span className="text-xs text-slate-600 dark:text-slate-300">
                  <span className="font-bold">Merge with my week</span>
                  <span className="block text-[11px] text-slate-500">
                    Recognizes what's already there, only adds what's new — no duplicates.
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2 rounded-lg p-1.5 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800/60">
                <input
                  type="radio"
                  name="import-strategy"
                  checked={importStrategy === 'replace'}
                  onChange={() => setImportStrategy('replace')}
                  className="mt-0.5 h-3.5 w-3.5 border-slate-300 dark:border-slate-700 text-brand-500 focus:ring-0 focus:ring-offset-0"
                />
                <span className="text-xs text-slate-600 dark:text-slate-300">
                  <span className="font-bold">Start fresh</span>
                  <span className="block text-[11px] text-slate-500">
                    Clears the board and builds this week from this dump only.
                  </span>
                </span>
              </label>
            </div>
          </fieldset>
        )}

        {providerNotice && (
          <p className="rounded-xl border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-950/20 p-3 text-center text-xs text-amber-600 dark:text-amber-400">
            {providerNotice}
          </p>
        )}

        {voice.listening && (
          <p role="status" className="text-center text-xs text-slate-500 dark:text-slate-400">
            🎤 {DICTATION_HINT}
          </p>
        )}

        {voice.notice && (
          <p className="rounded-xl border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-950/20 p-3 text-center text-xs text-amber-600 dark:text-amber-400">
            🎤 {voice.notice}
          </p>
        )}
      </div>

      {/* Sticky CTA (V2 §4a-capture, register #25): whenever the dump has
          content the ONE action is pinned to the viewport bottom — it may
          never sit below the fold. */}
      <div
        className={`pt-6 ${
          canSubmit
            ? 'sticky bottom-0 z-10 -mx-5 bg-gradient-to-t from-slate-50 via-slate-50/95 to-transparent px-5 pb-3 dark:from-slate-950 dark:via-slate-950/95'
            : ''
        }`}
      >
        <button
          onClick={() => canSubmit && runMagicSort(text)}
          disabled={!canSubmit}
          className="w-full rounded-xl bg-brand-600 py-4 text-center text-sm font-bold text-white shadow-lg shadow-brand-600/30 transition-all hover:bg-brand-500 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-slate-100 dark:bg-slate-800 disabled:text-slate-500 disabled:shadow-none"
        >
          Organize My Week ⚡
        </button>
      </div>

      <ScanCameraModal
        open={cameraOpen}
        batchStickyMode={batchSticky}
        shotCount={scans.length}
        onCapture={attachScan}
        onClose={() => setCameraOpen(false)}
        onGallery={() => fileInputRef.current?.click()}
      />
    </div>
  );
}
